import { useState, type FormEvent, type JSX } from 'react';
import { ContactSchema } from '../lib/schemas/forms';
import type { Painting } from '../types/painting';

interface EnquiryProps {
  painting: Painting;
  onClose: () => void;
}

type FieldErrors = Partial<Record<'name' | 'email' | 'message', string[]>>;

export default function Enquiry({ painting, onClose }: EnquiryProps): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(`I'm interested in "${painting.title}".`);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sent, setSent] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const result = ContactSchema.safeParse({ name, email, message });
    if (!result.success) {
      setErrors(result.error.flatten().fieldErrors);
      return;
    }

    setErrors({});
    // TODO: no email delivery service is wired up yet (EmailJS vs. a small API
    // endpoint is still an open decision — see rewrite.md Phase 4). Once chosen,
    // send `result.data` there instead of just flipping this flag.
    setSent(true);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-md bg-white p-6">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold">Enquire about {painting.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-neutral-500 hover:text-neutral-800"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {sent ? (
          <p className="text-neutral-700">
            Thanks — your enquiry about &ldquo;{painting.title}&rdquo; has been noted. We&apos;ll get back to you soon.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div>
              <label htmlFor="enquiry-name" className="mb-1 block text-sm text-neutral-700">
                Name
              </label>
              <input
                id="enquiry-name"
                type="text"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                className="w-full rounded border border-neutral-300 px-3 py-2 text-sm"
              />
              {errors.name?.[0] !== undefined ? <p className="mt-1 text-xs text-red-600">{errors.name[0]}</p> : null}
            </div>

            <div>
              <label htmlFor="enquiry-email" className="mb-1 block text-sm text-neutral-700">
                Email
              </label>
              <input
                id="enquiry-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                className="w-full rounded border border-neutral-300 px-3 py-2 text-sm"
              />
              {errors.email?.[0] !== undefined ? (
                <p className="mt-1 text-xs text-red-600">{errors.email[0]}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="enquiry-message" className="mb-1 block text-sm text-neutral-700">
                Message
              </label>
              <textarea
                id="enquiry-message"
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                }}
                rows={4}
                className="w-full rounded border border-neutral-300 px-3 py-2 text-sm"
              />
              {errors.message?.[0] !== undefined ? (
                <p className="mt-1 text-xs text-red-600">{errors.message[0]}</p>
              ) : null}
            </div>

            <button type="submit" className="mt-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
              Send enquiry
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
